import { useState } from 'react'

import { Box, Button } from '@factorialco/deprecated-design-system'
import {
  CompetenciesCompetenciesFinderBulkCreateDocument,
  CompetenciesCompetenciesFinderInitialDocument,
} from 'generated/resources/graphql'

import Modal from 'modules/core/components/Modal'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useMutation } from 'modules/core/hooks/useMutation'
import { useQuery } from 'modules/core/hooks/useQuery'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import {
  type Competency,
  type CompetencyConfig,
  type SelectedCompetency,
  getNewCompetencyInputs,
  hasToBeCreated,
  selectionToCompanyCompetencies,
  useCompetencyFinder,
} from 'modules/competencies/hooks/useCompetencyFinder'
import { type CompetencySuggestionTarget } from 'modules/competencies/hooks/useFactorialCompetenciesSuggestions'

import { Combobox } from './Combobox'
import { CreateForm } from './CreateForm'
import { Suggestions } from './Suggestions'

export type { Competency }

type Props = {
  target?: CompetencySuggestionTarget
  currentCompetencies: CompetencyConfig[]
  onSubmit: (newCompetencies: Competency[]) => void | Promise<void>
  onClose: () => void
}

export const CompetenciesFinder: React.FC<Props> = ({
  target,
  currentCompetencies = [],
  onSubmit,
  onClose,
}) => {
  const { company } = useCurrentSession()

  const [showCreateForm, setShowCreateForm] = useState(false)
  const [createFormName, setCreateFormName] = useState('')
  const initialCompetencyIds = currentCompetencies.map((config) => config.id)
  const { data } = useQuery(CompetenciesCompetenciesFinderInitialDocument, {
    variables: { ids: initialCompetencyIds, companyId: company.id },
  })

  const canCreate = data?.competencies?._policies?.competenciesManageV2?.can || false
  const isRevampEnabled =
    data?.features.featuresConnection.edges.find(
      ({ node }) => node.name === 'dev_competencies_revamp'
    )?.node?.enabled || false

  const {
    selectedCompetencies,
    initialFactorialUuids,
    excludeUuidSuggestions,
    addCompetency,
    removeCompetency,
    syncCreated,
  } = useCompetencyFinder({
    initialConfig: currentCompetencies,
    fetchedCompetencies: data?.competencies?.competencies || [],
  })

  const [bulkCreate, { loading: bulkCreating }] = useMutation(
    CompetenciesCompetenciesFinderBulkCreateDocument,
    {
      extractorFn: (d) => d.competenciesMutations.bulkCreateCompetenciesCompetency,
    }
  )

  const handleSubmit = async () => {
    const competencyInputs = getNewCompetencyInputs(selectedCompetencies.filter(hasToBeCreated))
    if (!competencyInputs.length) {
      onSubmit(selectionToCompanyCompetencies(selectedCompetencies))
      return
    }

    const response = await bulkCreate({
      variables: {
        competencies: competencyInputs,
      },
    })
    const result = handleResult(response, () => {})
    if (result.success) {
      const competencies = syncCreated(result?.data?.competencies || [])
      onSubmit(selectionToCompanyCompetencies(competencies))
    } else {
      app.setFlash({
        type: 'wrong',
        message: i18n.t('errors.generic_error'),
      })
      onSubmit(selectionToCompanyCompetencies(selectedCompetencies))
    }
  }

  const handleCreateNew = (name = '') => {
    if (canCreate) {
      setCreateFormName(name)
      setShowCreateForm(true)
    }
  }

  const handleFormSubmit = (newCompetency: SelectedCompetency) => {
    addCompetency(newCompetency)
    setShowCreateForm(false)
  }

  const handleFormClose = () => {
    setShowCreateForm(false)
  }

  return (
    <Modal
      illustration="performance"
      title={i18n.t('competencies_v3.competencies_finder.title')}
      onClose={onClose}
      onSubmit={handleSubmit}
      footer={
        <>
          <Button type="secondary" onClick={onClose} loading={bulkCreating}>
            {i18n.t('common.cancel')}
          </Button>
          <Button submit loading={bulkCreating}>
            {i18n.t('competencies_v3.competencies_finder.assign_button')}
          </Button>
        </>
      }
    >
      <Box gap="s24">
        <Combobox
          useFactorialCompetencies={isRevampEnabled}
          selectedCompetencies={selectedCompetencies}
          canCreate={canCreate}
          onAdd={addCompetency}
          onRemove={removeCompetency}
          onCreateNew={handleCreateNew}
        />
        {showCreateForm && (
          <CreateForm name={createFormName} onSubmit={handleFormSubmit} onClose={handleFormClose} />
        )}
        {target && isRevampEnabled && (
          <Suggestions
            key={`target-${target.id}-${target.type}`}
            target={target}
            initialUuids={initialFactorialUuids}
            excludeUuids={excludeUuidSuggestions}
            onAdd={addCompetency}
          />
        )}
      </Box>
    </Modal>
  )
}
